import React, { useState, useEffect, useCallback } from "react";
import "../page/staffList.css";

import ModalDeleteConfirmation from "../../../components/modal/modalDeleteConfirmation";
import PaginationFooter from "../../../components/footer/paginationFooter";
import StaffHeader from "../component/staffHeader";
import ModalAddStaff from "../component/modalAddStaff";
import StaffTable from "../component/staffTable";
import { Constant } from "../../../helper/constant";
import { useNavigate } from "react-router-dom";

const StaffList = () => {
  // =====================
  // Constant Definition
  // =====================
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [isOpenModalAddStaff, setIsOpenModalAddStaff] = useState(false);
  const [selectedStaffData, setSelectedStaffData] = useState({
    staff_id: "",
    staff_name: "",
    staff_username: "",
    staff_salary: "",
    staff_bonus: "",
    staff_system_role: "",
  });
  const [staffData, setStaffData] = useState(null);
  const [isOpenModalDeleteStaff, setIsOpenModalDeleteStaff] = useState(false);
  const [idStaffToDelete, setIdStaffToDelete] = useState("");
  const [searchStaffKeyword, setSearchStaffKeyword] = useState("");

  // =====================
  // Function Definition
  // =====================

  const addNewStaff = () => {
    resetSelectedStaffDataValue();
    setIsOpenModalAddStaff(true);
  };

  const onEditStaff = (staffData) => {
    setSelectedStaffData(staffData);
    setIsOpenModalAddStaff(true);
  };

  const onDeleteStaff = (staffData) => {
    setIsOpenModalDeleteStaff(true);
    setIdStaffToDelete(staffData.staff_id);
  };

  const onDeleteStaffConfirmed = () => {
    setIsOpenModalDeleteStaff(false);
    deleteStaff();
  };

  const onCloseModalDeleteStaff = () => {
    setIdStaffToDelete("");
    setIsOpenModalDeleteStaff(false);
  };

  const onCloseModal = () => {
    resetSelectedStaffDataValue();
    setIsOpenModalAddStaff(false);
  };

  const onSubmitNewStaff = (staffData, isEditData) => {
    setIsOpenModalAddStaff(false);
    if (isEditData) {
      editStaff(staffData);
    } else {
      createNewStaff(staffData);
    }
  };

  const resetSelectedStaffDataValue = () => {
    setSelectedStaffData({
      staff_id: "",
      staff_name: "",
      staff_username: "",
      staff_salary: "",
      staff_bonus: "",
      staff_system_role: "",
    });
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  // =====================
  // Request Method
  // =====================

  const getStaffData = useCallback(async () => {
    try {
      setIsLoading(true);
      localStorage.setItem("searchValue", "");

      let url = Constant.BASE_API_URL + "/staf?page=" + page;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      if (!data.staff) {
        setStaffData([]);
      } else {
        setStaffData(data);
      }
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setStaffData([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, navigate]);

  const createNewStaff = async (staffData) => {
    try {
      setIsLoading(true);
      const response = await fetch(Constant.BASE_API_URL + "/staf", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          staff_name: staffData.staff_name,
          staff_username: staffData.staff_username,
          staff_salary: staffData.staff_salary,
          staff_bonus: staffData.staff_bonus,
          staff_system_role: staffData.staff_system_role,
          staff_password: staffData.staff_password,
        }),
      });
      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getStaffData();
    }
  };

  const editStaff = async (staffData) => {
    try {
      setIsLoading(true);
      const staffId = staffData.staff_id;
      const response = await fetch(Constant.BASE_API_URL + "/staf/" + staffId, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          staff_name: staffData.staff_name,
          staff_username: staffData.staff_username,
          staff_salary: staffData.staff_salary,
          staff_bonus: staffData.staff_bonus,
          staff_system_role: staffData.staff_system_role,
        }),
      });
      if (!response.ok) {
        alert("Gagal Mengubah Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getStaffData();
    }
  };

  const deleteStaff = async () => {
    try {
      setIsLoading(true);
      const staffId = idStaffToDelete;
      const response = await fetch(Constant.BASE_API_URL + "/staf/" + staffId, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      setIdStaffToDelete("");
      getStaffData();
    }
  };

  const searchStaff = (searchText) => {
    setSearchStaffKeyword(searchText);
    setPage(1);
  };

  const getStaffSearchData = useCallback(async () => {
    try {
      setIsLoading(true);

      let url = new URL(Constant.BASE_API_URL + "/staf/search");
      url.searchParams.append("keyword", searchStaffKeyword);
      url.searchParams.append("page", page);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      if (!data.staff) {
        setStaffData(null);
      } else {
        setStaffData(data);
      }
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setStaffData(null);
    } finally {
      setIsLoading(false);
    }
  }, [searchStaffKeyword, page, navigate]);

  // =====================
  // Use Effect Method
  // =====================

  useEffect(() => {
    if (searchStaffKeyword !== "") {
      getStaffSearchData();
    } else {
      getStaffData();
    }
  }, [searchStaffKeyword, getStaffData, getStaffSearchData]);

  // =====================
  // Render Method
  // =====================

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1>Staff</h1>
      <StaffHeader
        onAddStaffClicked={addNewStaff}
        onSearchStaff={searchStaff}
      />
      <StaffTable
        page={page}
        staffs={staffData.staff}
        onEditStaffClicked={onEditStaff}
        onDeleteStaffClicked={onDeleteStaff}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
      <ModalAddStaff
        isShowModal={isOpenModalAddStaff}
        onCloseModal={onCloseModal}
        onSubmitData={onSubmitNewStaff}
        staffObject={selectedStaffData}
        key={Date.now()}
      />

      <ModalDeleteConfirmation
        isShowModal={isOpenModalDeleteStaff}
        onCloseModal={onCloseModalDeleteStaff}
        onDeleteConfirmed={onDeleteStaffConfirmed}
        idToDelete={idStaffToDelete}
      />
    </div>
  );
};

export default StaffList;
